import { logger } from "../../utils/logger";
import { useState, useEffect, useRef } from 'react';
import { 
  Shield, 
  Sparkles, 
  Star, 
  Award, 
  Target, 
  Check, 
  CheckCircle2, 
  Users, 
  Globe2, 
  Briefcase, 
  TrendingUp, 
  Zap, 
  Clock, 
  Rocket, 
  Video 
} from 'lucide-react';
import { API_URL } from '../../utils/api';
import { ImageWithFallback } from '../figma/ImageWithFallback';

interface Logo {
  id: string;
  name: string;
  imageUrl: string;
  order: number;
}

interface LogoSection {
  badgeText: string;
  badgeIcon: string;
  title: string;
  description: string;
  visible: boolean;
  logos: Logo[];
}

const iconMap: { [key: string]: any } = {
  Shield, Sparkles, Star, Award, Target, Check, CheckCircle2,
  Users, Globe2, Briefcase, TrendingUp, Zap, Clock, Rocket, Video,
};

interface TrustedClientsProps {
  data?: LogoSection | null;
  loading?: boolean;
}

// ============================================
// SKELETON
// ============================================

const TrustedClientsSkeleton = () => (
  <section className="relative py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-hidden border-y border-gray-100">
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-10 space-y-4">
        <div className="flex justify-center">
          <div className="h-8 w-52 bg-gray-200 rounded-full relative overflow-hidden">
            <div className="absolute inset-0 shimmer" />
          </div>
        </div>
        <div className="h-7 bg-gray-200 rounded w-2/3 mx-auto relative overflow-hidden">
          <div className="absolute inset-0 shimmer" />
        </div>
        <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto relative overflow-hidden">
          <div className="absolute inset-0 shimmer" />
        </div>
      </div>
      <div className="bg-white/80 rounded-2xl p-8 sm:p-10 border border-white/40 shadow-lg">
        <div className="flex gap-12 overflow-hidden">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-12 w-32 flex-shrink-0 bg-gray-200 rounded-lg relative overflow-hidden">
              <div className="absolute inset-0 shimmer" />
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// ============================================
// LOGO ITEM
// ============================================

const LogoItem = ({ company }: { company: Logo }) => (
  <div className="relative flex-shrink-0 flex items-center justify-center px-10 h-12 border-r border-gray-100 last:border-r-0 group">
    {company.imageUrl ? (
      <ImageWithFallback
        src={company.imageUrl}
        alt={company.name}
        className="h-full w-auto max-w-[120px] object-contain opacity-50 group-hover:opacity-100 transition-opacity duration-300"
        fallback={
          <span className="text-gray-300 group-hover:text-gray-500 font-medium text-sm transition-colors duration-300 whitespace-nowrap">
            {company.name}
          </span>
        }
      />
    ) : (
      <span className="text-gray-300 group-hover:text-gray-500 font-medium text-sm transition-colors duration-300 whitespace-nowrap">
        {company.name}
      </span>
    )}
  </div>
);

// ============================================
// COMPONENT
// ============================================

export function TrustedClients({ data: propsData, loading: propsLoading }: TrustedClientsProps = {}) {
  const defaultData: LogoSection = {
    badgeText: 'Trusted By Industry Leaders',
    badgeIcon: 'Shield',
    title: 'Powering Innovation Across Manufacturing, Energy & Automotive',
    description: '280+ companies trust us with their most complex technical communications',
    visible: true,
    logos: [
      { id: '1', name: 'Boeing', imageUrl: '', order: 1 },
      { id: '2', name: 'Caterpillar', imageUrl: '', order: 2 },
      { id: '3', name: 'Tesla', imageUrl: '', order: 3 },
      { id: '4', name: 'Siemens', imageUrl: '', order: 4 },
      { id: '5', name: 'Pfizer', imageUrl: '', order: 5 },
      { id: '6', name: 'GE', imageUrl: '', order: 6 },
      { id: '7', name: 'Shell', imageUrl: '', order: 7 },
      { id: '8', name: 'Ford', imageUrl: '', order: 8 },
    ]
  };

  const [logoData, setLogoData] = useState<LogoSection | null>(null);
  const [loading, setLoading] = useState(true);

  const trackRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<number>(0);
  const xRef = useRef(0);
  const pausedRef = useRef(false);
  const lastRef = useRef<number | null>(null);

  const fetchLogoData = async () => {
    try {
      let response = await fetch(`${API_URL}/content/trusted-clients`, {
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok && response.status === 404) {
        response = await fetch(`${API_URL}/homepage/logos`, {
          headers: { 'Content-Type': 'application/json' },
        });
      }

      if (!response.ok) {
        setLogoData(defaultData);
        return;
      }

      const result = await response.json();

      if (result.success && result.data) {
        setLogoData(result.data);
      } else {
        setLogoData(defaultData);
      }
    } catch (error: any) {
      logger.log('ℹ️ [TRUSTED CLIENTS] Fetch failed (network or CORS), using defaults');
      setLogoData(defaultData);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (propsData) {
      setLogoData(propsData);
      setLoading(false);
      return;
    }

    fetchLogoData();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !propsData) {
        fetchLogoData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [propsData]);

  // Start scroll animation + drag-to-scroll once data is ready
  useEffect(() => {
    if (!trackRef.current) return;

    const track = trackRef.current;
    let isDragging = false;
    let dragStartX = 0;
    let dragStartOffset = 0;
    let velocity = 0;
    let lastDragX = 0;
    let lastDragTime = 0;

    // ── Mouse hover (pause auto-scroll when not dragging) ──
    const onEnter = () => { if (!isDragging) pausedRef.current = true; };
    const onLeave = () => { if (!isDragging) pausedRef.current = false; };
    track.addEventListener('mouseenter', onEnter);
    track.addEventListener('mouseleave', onLeave);

    // ── Drag start ──
    const onMouseDown = (e: MouseEvent) => {
      isDragging = true;
      pausedRef.current = true;
      dragStartX = e.clientX;
      dragStartOffset = xRef.current;
      lastDragX = e.clientX;
      lastDragTime = performance.now();
      velocity = 0;
      track.style.cursor = 'grabbing';
      e.preventDefault();
    };

    // ── Drag move ──
    const onMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      const now = performance.now();
      const dt = now - lastDragTime;
      const dx = e.clientX - lastDragX;

      // Track velocity for momentum
      if (dt > 0) velocity = dx / dt;
      lastDragX = e.clientX;
      lastDragTime = now;

      const delta = e.clientX - dragStartX;
      xRef.current = dragStartOffset + delta;

      // Keep within seamless bounds
      const segment = track.scrollWidth / 3;
      while (xRef.current > 0) xRef.current -= segment;
      while (Math.abs(xRef.current) >= segment) xRef.current += segment;

      track.style.transform = `translateX(${xRef.current}px)`;
    };

    // ── Drag end — apply momentum then resume auto-scroll ──
    const onMouseUp = () => {
      if (!isDragging) return;
      isDragging = false;
      track.style.cursor = 'grab';

      // Momentum glide
      let momentum = velocity * 12;
      const glide = () => {
        if (Math.abs(momentum) < 0.1) {
          // Resume auto-scroll
          pausedRef.current = false;
          track.style.cursor = '';
          return;
        }
        xRef.current += momentum;
        momentum *= 0.92;
        const segment = track.scrollWidth / 3;
        while (xRef.current > 0) xRef.current -= segment;
        while (Math.abs(xRef.current) >= segment) xRef.current += segment;
        track.style.transform = `translateX(${xRef.current}px)`;
        requestAnimationFrame(glide);
      };
      requestAnimationFrame(glide);
    };

    // ── Touch support ──
    const onTouchStart = (e: TouchEvent) => {
      isDragging = true;
      pausedRef.current = true;
      dragStartX = e.touches[0].clientX;
      dragStartOffset = xRef.current;
      lastDragX = e.touches[0].clientX;
      lastDragTime = performance.now();
      velocity = 0;
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!isDragging) return;
      const now = performance.now();
      const dt = now - lastDragTime;
      const dx = e.touches[0].clientX - lastDragX;
      if (dt > 0) velocity = dx / dt;
      lastDragX = e.touches[0].clientX;
      lastDragTime = now;
      const delta = e.touches[0].clientX - dragStartX;
      xRef.current = dragStartOffset + delta;
      const segment = track.scrollWidth / 3;
      while (xRef.current > 0) xRef.current -= segment;
      while (Math.abs(xRef.current) >= segment) xRef.current += segment;
      track.style.transform = `translateX(${xRef.current}px)`;
    };

    const onTouchEnd = () => onMouseUp();

    track.addEventListener('mousedown', onMouseDown);
    track.addEventListener('touchstart', onTouchStart, { passive: true });
    track.addEventListener('touchmove', onTouchMove, { passive: true });
    track.addEventListener('touchend', onTouchEnd);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);

    // Set grab cursor to hint draggability
    track.style.cursor = 'grab';

    // ── Auto-scroll animation loop ──
    const animate = (ts: number) => {
      if (!lastRef.current) lastRef.current = ts;
      const dt = Math.min(ts - lastRef.current, 32);
      lastRef.current = ts;

      if (!pausedRef.current && track) {
        xRef.current -= 0.45 * (dt / 16.67);
        const segment = track.scrollWidth / 3;
        if (Math.abs(xRef.current) >= segment) xRef.current += segment;
        track.style.transform = `translateX(${xRef.current}px)`;
      }

      animRef.current = requestAnimationFrame(animate);
    };

    animRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animRef.current);
      track.removeEventListener('mouseenter', onEnter);
      track.removeEventListener('mouseleave', onLeave);
      track.removeEventListener('mousedown', onMouseDown);
      track.removeEventListener('touchstart', onTouchStart);
      track.removeEventListener('touchmove', onTouchMove);
      track.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [logoData]);

  if (loading) {
    return <TrustedClientsSkeleton />;
  }

  const data = logoData || defaultData;
  const BadgeIcon = iconMap[data.badgeIcon] || Shield;

  if (!data.visible) {
    return null;
  }

  const sorted = [...data.logos].sort((a, b) => a.order - b.order);

  return (
    <section className="relative py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-hidden border-y border-gray-100">
      {/* Background accents */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-10 left-10 w-96 h-96 bg-gradient-to-br from-[#E87D3E] via-[#E8B84D] to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-gradient-to-tr from-[#2F2E5C] via-[#4A4878] to-transparent rounded-full blur-3xl" />
      </div>
      <div className="absolute inset-0 opacity-[0.01]" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 400 400\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' /%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\' /%3E%3C/svg%3E")'}} />

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-white/60 backdrop-blur-xl px-4 py-2 rounded-full mb-4 border border-white/40 shadow-lg">
            <BadgeIcon className="w-4 h-4 text-[#E87D3E]" />
            <span className="text-sm font-semibold bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] bg-clip-text text-transparent">
              {data.badgeText}
            </span>
          </div>
          <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-[#2F2E5C] mb-3">
            {data.title}
          </h2>
          <p className="text-base text-gray-600 max-w-2xl mx-auto">
            {data.description}
          </p>
        </div>

        {/* Logo strip card */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-white/40 shadow-lg overflow-hidden">
          <div className="relative py-8">
            {/* Fade edges */}
            <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-white/90 to-transparent z-10 pointer-events-none" />
            <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-white/90 to-transparent z-10 pointer-events-none" />

            {/* Scrolling track — 3 identical sets for seamless loop */}
            <div ref={trackRef} className="flex items-center">
              {[0, 1, 2].map((setIndex) =>
                sorted.map((company) => (
                  <LogoItem key={`set-${setIndex}-${company.id}`} company={company} />
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}